/**
 * Error Logger Service
 *
 * Main service that acts as a facade for error logging.
 * Routes to appropriate logger based on environment configuration.
 */

import {
  IErrorLogger,
  ErrorContext,
  ApiErrorContext,
  ReactErrorContext,
  ErrorBreadcrumb,
  ContextualError,
  ErrorType,
} from '@shared/types/ErrorLogging';
import { getErrorLoggingConfig } from '@shared/config/errorLogging';
import { DevelopmentLogger } from '@shared/loggers/DevelopmentLogger';
import { SentryLogger } from '@shared/loggers/SentryLogger';
import { ContextCollector } from '@shared/utils/errorUtils';

/**
 * Singleton ErrorLogger service
 */
class ErrorLoggerService implements IErrorLogger {
  private logger: IErrorLogger;
  private config = getErrorLoggingConfig();

  constructor() {
    this.logger = this.createLogger();
  }

  /**
   * Logs an error with context
   */
  logError(
    error: Error | ContextualError,
    context?: Partial<ErrorContext>
  ): void {
    try {
      this.logger.logError(error, context);
    } catch (loggingError) {
      // Fallback to console if logger fails
      console.error('Error logger failed:', loggingError);
      console.error('Original error:', error);
    }
  }

  /**
   * Logs a warning
   */
  logWarning(message: string, context?: Partial<ErrorContext>): void {
    try {
      this.logger.logWarning(message, context);
    } catch (loggingError) {
      console.warn('Warning logger failed:', loggingError);
      console.warn('Original warning:', message);
    }
  }

  /**
   * Logs informational message
   */
  logInfo(message: string, context?: Partial<ErrorContext>): void {
    try {
      this.logger.logInfo(message, context);
    } catch (loggingError) {
      console.info('Info logger failed:', loggingError);
      console.info('Original info:', message);
    }
  }

  /**
   * Logs debug information
   */
  logDebug(message: string, context?: Partial<ErrorContext>): void {
    try {
      this.logger.logDebug(message, context);
    } catch (loggingError) {
      console.debug('Debug logger failed:', loggingError);
      console.debug('Original debug:', message);
    }
  }

  /**
   * Logs API-specific errors
   */
  logApiError(error: Error, apiContext: ApiErrorContext): void {
    try {
      this.logger.logApiError(error, apiContext);
    } catch (loggingError) {
      console.error('API error logger failed:', loggingError);
      console.error('Original API error:', error);
    }
  }

  /**
   * Logs React-specific errors
   */
  logReactError(error: Error, reactContext: ReactErrorContext): void {
    try {
      this.logger.logReactError(error, reactContext);
    } catch (loggingError) {
      console.error('React error logger failed:', loggingError);
      console.error('Original React error:', error);
    }
  }

  /**
   * Adds a breadcrumb
   */
  addBreadcrumb(breadcrumb: ErrorBreadcrumb): void {
    try {
      this.logger.addBreadcrumb(breadcrumb);
    } catch (loggingError) {
      console.warn('Breadcrumb logger failed:', loggingError);
    }
  }

  /**
   * Sets user context
   */
  setUserContext(userId: string, userEmail?: string): void {
    try {
      this.logger.setUserContext(userId, userEmail);
    } catch (loggingError) {
      console.warn('Set user context failed:', loggingError);
    }
  }

  /**
   * Clears user context
   */
  clearUserContext(): void {
    try {
      this.logger.clearUserContext();
    } catch (loggingError) {
      console.warn('Clear user context failed:', loggingError);
    }
  }

  /**
   * Creates a contextual error with enriched information
   */
  createContextualError(
    message: string,
    type: ErrorType,
    originalError?: Error,
    additionalContext?: Partial<ErrorContext>
  ): ContextualError {
    const baseContext = ContextCollector.collectErrorContext();
    const context: ErrorContext = {
      ...baseContext,
      type,
      ...additionalContext,
    };

    return new ContextualError(message, context, originalError);
  }

  /**
   * Convenience method for logging API errors with automatic context collection
   */
  logApiErrorWithContext(
    error: Error,
    method: string,
    endpoint: string,
    status?: number,
    requestData?: any,
    responseData?: any
  ): void {
    const apiContext: ApiErrorContext = {
      type: 'api_error',
      method,
      endpoint,
      status,
      requestBody: requestData,
      responseBody: responseData,
      ...ContextCollector.collectErrorContext(),
    };

    this.logApiError(error, apiContext);
  }

  /**
   * Convenience method for logging component errors
   */
  logComponentError(
    error: Error,
    componentName: string,
    action?: string,
    additionalContext?: Record<string, any>
  ): void {
    const contextualError = this.createContextualError(
      `Component error in ${componentName}: ${error.message}`,
      'component_error',
      error,
      {
        component: componentName,
        action,
        metadata: additionalContext,
      }
    );

    this.logError(contextualError);
  }

  /**
   * Convenience method for logging hook errors
   */
  logHookError(
    error: Error,
    hookName: string,
    additionalContext?: Record<string, any>
  ): void {
    const contextualError = this.createContextualError(
      `Hook error in ${hookName}: ${error.message}`,
      'hook_error',
      error,
      {
        feature: hookName,
        metadata: additionalContext,
      }
    );

    this.logError(contextualError);
  }

  /**
   * Creates the appropriate logger based on environment
   */
  private createLogger(): IErrorLogger {
    if (this.config.enableSentryLogging && this.config.enableConsoleLogging) {
      // Both enabled - create a composite logger
      return new CompositeLogger([new DevelopmentLogger(), new SentryLogger()]);
    } else if (this.config.enableSentryLogging) {
      return new SentryLogger();
    } else {
      return new DevelopmentLogger();
    }
  }
}

/**
 * Composite logger that delegates to multiple loggers
 */
class CompositeLogger implements IErrorLogger {
  constructor(private loggers: IErrorLogger[]) {}

  logError(
    error: Error | ContextualError,
    context?: Partial<ErrorContext>
  ): void {
    this.loggers.forEach(logger => {
      try {
        logger.logError(error, context);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  logWarning(message: string, context?: Partial<ErrorContext>): void {
    this.loggers.forEach(logger => {
      try {
        logger.logWarning(message, context);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  logInfo(message: string, context?: Partial<ErrorContext>): void {
    this.loggers.forEach(logger => {
      try {
        logger.logInfo(message, context);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  logDebug(message: string, context?: Partial<ErrorContext>): void {
    this.loggers.forEach(logger => {
      try {
        logger.logDebug(message, context);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  logApiError(error: Error, apiContext: ApiErrorContext): void {
    this.loggers.forEach(logger => {
      try {
        logger.logApiError(error, apiContext);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  logReactError(error: Error, reactContext: ReactErrorContext): void {
    this.loggers.forEach(logger => {
      try {
        logger.logReactError(error, reactContext);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  addBreadcrumb(breadcrumb: ErrorBreadcrumb): void {
    this.loggers.forEach(logger => {
      try {
        logger.addBreadcrumb(breadcrumb);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  setUserContext(userId: string, userEmail?: string): void {
    this.loggers.forEach(logger => {
      try {
        logger.setUserContext(userId, userEmail);
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }

  clearUserContext(): void {
    this.loggers.forEach(logger => {
      try {
        logger.clearUserContext();
      } catch (err) {
        console.warn('Logger failed:', err);
      }
    });
  }
}

// Export singleton instance
export const errorLogger = new ErrorLoggerService();
export default errorLogger;
